import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, ShieldCheck, CheckCircle, Sparkles, Play } from 'lucide-react';
import { fetchBySlug } from '../lib/api';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function TreatmentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    async function loadTreatment() {
      setLoading(true);
      try {
        const data = await fetchBySlug(slug);
        if (data && data.collectionName === 'treatments') {
          setTreatment(data);
        } else {
          // If not found or not a treatment, redirect to treatments list
          console.warn('Treatment not found for slug:', slug);
          setTreatment(null);
        }
      } catch (error) {
        console.error('Error loading treatment detail:', error);
      } finally {
        setLoading(false);
      }
    }
    loadTreatment();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-10 h-10 border-4 border-[#5F7464] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!treatment) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#FAF9F6] px-4 text-center space-y-4">
        <h1 className="font-serif text-2xl font-bold text-[#2C3531]">Behandlingen blev ikke fundet</h1>
        <p className="text-sm text-[#5C6360] max-w-md">
          Siden eller den specifikke behandlingsform, du leder efter, kunne desværre ikke findes. Den kan være flyttet eller slettet.
        </p>
        <Link
          to="/behandlinger"
          className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#5F7464] text-white text-sm font-medium hover:bg-[#4E6052] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Se alle behandlinger
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{treatment.title} | Klinik Acu-sense</title>
        <meta name="description" content={`Læs om ${treatment.title} hos Klinik Acu-sense i Hammel. Professionel behandling ved RAB-godkendt akupunktør Brian Hangaard.`} />
        <meta property="og:title" content={`${treatment.title} | Klinik Acu-sense`} />
        <meta property="og:description" content={`Læs om ${treatment.title} hos Klinik Acu-sense i Hammel. Professionel behandling ved RAB-godkendt akupunktør Brian Hangaard.`} />
        {treatment.image?.[0] && <meta property="og:image" content={treatment.image[0]} />}
        <link rel="canonical" href={`https://www.acu-sense.dk/behandlinger/${slug}`} />
      </Helmet>

      {/* Back navigation */}
      <div className="bg-[#FAF9F6] pt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/behandlinger"
            className="inline-flex items-center text-sm font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbage til behandlinger
          </Link>
        </div>
      </div>

      {/* Detail Section */}
      <section className="py-12 bg-[#FAF9F6] pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white rounded-3xl border border-[#E6E4DF] overflow-hidden shadow-sm">
            {/* Treatment Image */}
            {treatment.image?.[0] && (
              <div className="h-[300px] sm:h-[400px] relative overflow-hidden">
                <img
                  src={treatment.image[0]}
                  alt={treatment.title}
                  className="w-full h-full object-cover"
                />
                {treatment.is_seasonal && (
                  <div className="absolute top-6 left-6 bg-[#5F7464] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1.5 shadow-md">
                    <Sparkles className="w-3.5 h-3.5" />
                    Sæsonbehandling
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-6 sm:p-10 md:p-12 space-y-8">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#8FA89B]/10 text-[#3D4F44] text-xs font-semibold uppercase tracking-wider">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#5F7464]" />
                    RAB-godkendt
                  </span>
                  {treatment.price && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F3F1EC] text-[#2C3531] text-xs font-semibold uppercase tracking-wider">
                      {treatment.price}
                    </span>
                  )}
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C3531] tracking-tight">
                  {treatment.title}
                </h1>
              </div>

              {/* Description body */}
              <div
                className="prose prose-stone max-w-none text-sm sm:text-base text-[#4A504D] leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: treatment.description }}
              />

              {/* Video Section */}
              {(treatment.video || (treatment.slug && (typeof treatment.slug === 'object' ? treatment.slug._ : treatment.slug) === 'seizen-hari')) && (
                <div className="mt-10 pt-8 border-t border-[#E6E4DF] space-y-4">
                  <h3 className="font-serif text-xl font-bold text-[#2C3531] flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#5F7464]" />
                    Videoeksempel: Knæbehandling med Seizen Hari
                  </h3>
                  <p className="text-sm sm:text-base text-[#5C6360] leading-relaxed">
                    Her kan du se et videoeksempel på en skånsom og fuldstændig smertefri knæbehandling med den japanske meridianterapi Seizen Hari. Behandlingen udføres blidt på hudens overflade uden indstik af nåle.
                  </p>
                  <div className="relative rounded-2xl overflow-hidden border border-[#E6E4DF] bg-black aspect-video shadow-md max-w-2xl mx-auto group">
                    <video
                      ref={videoRef}
                      src={treatment.video || "https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/knae.mp4"}
                      controls
                      playsInline
                      preload="none"
                      className="w-full h-full object-cover"
                      poster="https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/22108187-2392-4517-9a2f-8a5b1c94289f.webp"
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />
                    {!isPlaying && (
                      <button
                        onClick={handlePlayVideo}
                        className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors duration-300 group-focus:outline-none"
                        aria-label="Afspil video"
                      >
                        <div className="w-16 h-16 rounded-full bg-[#5F7464] text-white flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                          <Play className="w-8 h-8 fill-current translate-x-0.5" />
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Subsidies & Booking Box */}
              <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#FAF9F6] border border-[#E6E4DF] space-y-6">
                <h3 className="font-serif text-lg font-bold text-[#2C3531] flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#5F7464]" />
                  Praktisk information & tilskud
                </h3>
                <ul className="space-y-3 text-sm text-[#5C6360]">
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5F7464] mt-2 shrink-0" />
                    <span>Der ydes tilskud fra <strong>Sygeforsikringen "danmark"</strong> til denne behandling.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5F7464] mt-2 shrink-0" />
                    <span>De fleste private <strong>sundhedsforsikringer</strong> dækker behandlingen helt eller delvist.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#5F7464] mt-2 shrink-0" />
                    <span>Behandlingen tilpasses altid individuelt efter dine symptomer og kropslige ubalancer.</span>
                  </li>
                </ul>

                <div className="pt-4 border-t border-[#E6E4DF] flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-center sm:text-left">
                    <span className="text-xs text-[#8A908D] block uppercase tracking-wider font-bold">Pris</span>
                    <span className="text-base font-bold text-[#2C3531]">{treatment.price || 'Se prisliste'}</span>
                  </div>
                  <a
                    href={BOOKING_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-md"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book tid online
                  </a>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
